/**
 * CoverageTabPanel — wraps the existing review-agent CoverageTab in
 * a Variant-C-styled container.
 *
 * Reuse playbook: per the review-agent README, "adding a third IA is
 * a one-line addition, not a new implementation." This file is that
 * one-line addition. Zero coverage rendering logic; we just construct
 * the sidecar's sessionId convention and hand off.
 *
 * SRP: shape adapter only — converts (trial, session, moduleId) into
 * the sidecar's <trial>__<session> sessionId and mounts CoverageTab.
 *
 * Empty state: when run identity hasn't arrived yet (run_metadata
 * SSE event lands ~500ms post-start), we show a brief hint rather
 * than mounting CoverageTab with a junk sessionId.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { CoverageTab } from "../../../components/review-agent/CoverageTab";
import type { RunIdentity } from "../../sseAdapter";
import { fonts, inkColors } from "../../tokens";

interface CoverageTabPanelProps {
  identity: RunIdentity | null;
  moduleId: string;
}

const useStyles = makeStyles({
  hint: {
    padding: "20px 16px",
    fontFamily: fonts.body,
    fontSize: "11.5px",
    color: inkColors.ink4,
    fontStyle: "italic",
    lineHeight: 1.5,
  },
});

export const CoverageTabPanel: React.FC<CoverageTabPanelProps> = ({ identity, moduleId }) => {
  const styles = useStyles();
  if (!identity) {
    return (
      <div className={styles.hint}>
        Coverage data appears once the run reports its identity (a beat after the first section starts).
      </div>
    );
  }
  const sessionId = `${identity.trial}__${identity.session}`;
  return <CoverageTab sessionId={sessionId} moduleId={moduleId} />;
};

export default CoverageTabPanel;
